import React, { useState } from "react";
import toast from "react-hot-toast";
import { 
  useUpdateFeedbackStatusMutation
} from "../../../../store/apis/helpCenter.api";
import {
  Modal2,
  Modal2Footer,
  TextArea,
  Select,
  Button,
  Badge
} from "../../../common";
import { Eye, Clock, CheckCircle2, AlertCircle, User, Mail, FileCode, MessageSquare, FileText } from "lucide-react";

interface TicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: any;
}

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  ticket 
}) => {
  const [updateStatus, { isLoading: isUpdating }] = useUpdateFeedbackStatusMutation();
  const [status, setStatus] = useState<string>(ticket.status || "OPEN");

  const handleUpdate = async () => {
    try {
      await updateStatus({ 
        id: ticket._id || ticket.id, 
        status
      }).unwrap();
      toast.success("Feedback status updated successfully");
      onClose();
    } catch (error) {
      console.error("Failed to update feedback status:", error);
      toast.error("Failed to update feedback status");
    }
  };

  const statusOptions = [
    { value: "OPEN" as any, label: "Open" },
    { value: "CLOSED" as any, label: "Closed" },
  ];

  return (
    <Modal2
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span>Feedback Support</span>
        </div>
      }
      width={880}
      footer={
        <Modal2Footer>
          <Button appearance="secondary" onClick={onClose} disabled={isUpdating}>
            Close
          </Button>
          <Button
            appearance="primary"
            onClick={handleUpdate}
            isLoading={isUpdating}
          >
            Update Status
          </Button>
        </Modal2Footer>
      }
    >
      <div className="space-y-6 py-2">
        {/* Ticket Header Info */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-5 bg-[var(--color-bg-muted)] rounded-[12px] border border-[var(--color-border-primary)]">
          <div className="space-y-1">
            <span className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Employee Name</span>
            <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
              <User className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              <span className="text-sm font-semibold">{ticket.name || 'Anonymous'}</span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Employee Email</span>
            <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
              <Mail className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              <span className="text-sm font-semibold">{ticket.email || 'N/A'}</span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Module Category</span>
            <div className="flex items-center gap-2">
              <FileCode className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              <Badge variant="blue" size="middle">
                {ticket.moduleCode || 'General'}
              </Badge>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Related Article</span>
            <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
              <FileText className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              <span className="text-sm font-semibold truncate max-w-[200px]">
                {ticket.articleTitle || 'None'}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Submitted On</span>
            <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
              <Clock className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              <span className="text-sm font-semibold">
                {ticket.createdAt 
                  ? (() => {
                      const date = new Date(ticket.createdAt);
                      const d = String(date.getDate()).padStart(2, '0');
                      const m = String(date.getMonth() + 1).padStart(2, '0');
                      const y = date.getFullYear();
                      const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                      return `${d}/${m}/${y}, ${time}`;
                    })()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Query Content */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[var(--color-text-tertiary)]" />
            <label className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Issue Description</label>
          </div>
          <div className="p-4 bg-[var(--color-bg-muted)] border border-[var(--color-border-primary)] rounded-[12px] text-[var(--color-text-secondary)] text-sm leading-relaxed">
            {ticket.description}
          </div>
        </div>

        {/* Management Controls */}
        <div className="pt-4 border-t border-[var(--color-border-tertiary)] space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[var(--color-text-tertiary)]" />
            <label className="text-caption uppercase font-bold text-[var(--color-text-tertiary)] tracking-wider">Current Status</label>
          </div>
          <Select
            value={status}
            onChange={(val: any) => setStatus(val)}
            options={statusOptions}
            required
            className="max-w-xs"
          />
        </div>
      </div>
    </Modal2>
  );
};

export default TicketDetailModal;
